import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Mail, CreditCard, Cake, CalendarDays } from 'lucide-react';
import logoImg from './4sightlogo.png';
import api from '../services/api';

const DEPT_COLORS = {
  'research': '#0ea5e9',
  'data processing': '#8b5cf6',
  'data operations': '#7c3aed',
  'scripting': '#f59e0b',
  'technology': '#f59e0b',
  'accounts': '#10b981',
  'finance': '#10b981',
  'hr': '#ec4899',
  'operations': '#f97316',
};

export function deptColor(dept) {
  return DEPT_COLORS[(dept || '').toLowerCase()] || '#0d9488';
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (parts[0]?.[0] || '?').toUpperCase();
}

export function IDCard({ employee }) {
  const empId = employee.employee_number || `EMP-${String(employee.id).padStart(4, '0')}`;
  const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
  const fallbackSrc = `https://ui-avatars.com/api/?name=${encodeURIComponent(employee.name || '?')}&background=1e295c&color=fff&size=256&bold=true`;

  const rows = [
    { bg: '#dbeafe', icon: <Mail size={15} color="#2563eb" />, text: employee.email },
    { bg: '#e0e7ff', icon: <CreditCard size={15} color="#4338ca" />, text: empId },
    { bg: '#fef3c7', icon: <Cake size={15} color="#d97706" />, text: fmtD(employee.date_of_birth), muted: 'DOB not set' },
    { bg: '#ede9fe', icon: <CalendarDays size={15} color="#7c3aed" />, text: fmtD(employee.date_of_joining), muted: 'DOJ not set' },
  ];

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');`}</style>
      <div className="relative overflow-hidden rounded-3xl bg-white select-none"
        style={{ width: 330, fontFamily: "'Poppins',system-ui,sans-serif", boxShadow: '0 25px 50px -12px rgba(15,23,42,0.35),0 0 0 1px rgba(0,0,0,0.05)' }}>

        {/* Glass shimmer overlay */}
        <div className="absolute inset-0 pointer-events-none z-10"
          style={{ background: 'linear-gradient(115deg,rgba(255,255,255,0.2) 10%,transparent 40%,transparent 60%,rgba(255,255,255,0.05) 90%)', mixBlendMode: 'overlay' }} />

        {/* TOP — Deep Navy */}
        <div className="relative text-center text-white" style={{ background: 'radial-gradient(circle at 50% 0%,#1e295c 0%,#0a1128 75%)', padding: '22px 20px 28px' }}>
          <div className="flex items-center gap-2">
            <img src={logoImg} alt="4Sight" style={{ width: 26, height: 26, borderRadius: 6, objectFit: 'contain', background: '#fff', padding: 2 }} />
            <span className="uppercase font-bold text-left" style={{ fontSize: 11, letterSpacing: '0.05em', color: 'rgba(255,255,255,0.9)' }}>
              4SIGHT RESEARCH &amp; ANALYTICS FZCO
            </span>
          </div>

          <div style={{ height: 2, margin: '12px -20px 0', background: 'linear-gradient(90deg,transparent,#f6e17a 20%,#fff3c4 50%,#f6e17a 80%,transparent)' }} />

          <div className="mx-auto rounded-full" style={{ width: 124, height: 124, marginTop: 22, padding: 5, background: 'conic-gradient(from 180deg at 50% 50%,#b8860b,#ffd700,#fff8dc,#daa520,#8b6508,#ffd700,#b8860b)', boxShadow: '0 10px 25px rgba(0,0,0,0.4)' }}>
            <img src={employee.photo_url || fallbackSrc} alt={employee.name}
              className="w-full h-full rounded-full object-cover block"
              style={{ border: '2px solid #0a1128' }}
              onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackSrc; }} />
          </div>

          <h2 className="text-white" style={{ margin: '14px 0 2px', fontSize: 21, fontWeight: 800, letterSpacing: '0.02em' }}>{employee.name}</h2>
          <p style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.7)', margin: '0 0 14px' }}>{employee.designation || '—'}</p>
          {employee.department && (
            <span className="inline-block font-bold uppercase"
              style={{ padding: '6px 20px', borderRadius: 99, fontSize: 11, letterSpacing: '0.08em', color: '#1e1e1e', background: 'linear-gradient(180deg,#fff 0%,#e2e8f0 50%,#cbd5e1 100%)', boxShadow: '0 4px 10px rgba(0,0,0,0.2),inset 0 1px 1px rgba(255,255,255,0.9)' }}>
              {employee.department.toUpperCase()}
            </span>
          )}
        </div>

        {/* BOTTOM — Brushed Metallic Silver */}
        <div className="relative flex flex-col overflow-hidden"
          style={{ padding: '24px 20px 20px', gap: 14, background: 'repeating-linear-gradient(100deg,rgba(255,255,255,0.5) 0px,rgba(255,255,255,0) 1px,rgba(0,0,0,0.04) 2px,rgba(255,255,255,0) 3px),linear-gradient(135deg,#f8fafc 0%,#cbd5e1 55%,#eef2f6 100%)' }}>
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(120deg,transparent 30%,rgba(255,255,255,0.55) 48%,transparent 65%)' }} />
          {rows.map((row, i) => (
            <div key={i} className="flex items-center" style={{ gap: 12 }}>
              <div className="flex items-center justify-center flex-shrink-0 rounded-full"
                style={{ width: 32, height: 32, background: row.bg, boxShadow: '0 2px 5px rgba(0,0,0,0.08),inset 0 1px 0 rgba(255,255,255,0.8)' }}>
                {row.icon}
              </div>
              {row.text
                ? <span className="font-semibold" style={{ fontSize: 13, color: '#1e293b', wordBreak: 'break-all' }}>{row.text}</span>
                : <span style={{ fontSize: 12, fontWeight: 500, color: '#94a3b8', fontStyle: 'italic' }}>{row.muted}</span>}
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className="relative flex items-center justify-between overflow-hidden"
          style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.4)', background: 'repeating-linear-gradient(100deg,rgba(255,255,255,0.4) 0px,rgba(255,255,255,0) 1px,rgba(0,0,0,0.05) 2px,rgba(255,255,255,0) 3px),linear-gradient(180deg,#cbd5e1 0%,#94a3b8 100%)' }}>
          <span className="uppercase font-bold" style={{ fontSize: 10, letterSpacing: '0.05em', color: '#334155' }}>4sightglobal.com</span>
          <div className="flex items-center justify-center flex-shrink-0"
            style={{ width: 44, height: 44, marginTop: -26, borderRadius: 14, background: 'linear-gradient(160deg,#24356b,#0a1128)', boxShadow: '0 4px 8px rgba(0,0,0,0.3),0 0 0 3px #e2e6ec' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round">
              <path d="M3 21V12a9 9 0 0 1 18 0v9" /><path d="M7 21v-7a5 5 0 0 1 10 0v7" />
            </svg>
          </div>
          <span className="uppercase font-bold" style={{ fontSize: 10, letterSpacing: '0.05em', color: '#334155' }}>Employee ID</span>
        </div>
      </div>
    </>
  );
}

export default function IDCardPage() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [captureEmpNum, setCaptureEmpNum] = useState('');
  const [captureDob, setCaptureDob] = useState('');
  const [captureDoj, setCaptureDoj] = useState('');
  const [capturePhoto, setCapturePhoto] = useState(null);
  const [captureSaving, setCaptureSaving] = useState(false);
  const photoInputRef = useRef(null);
  const isAdmin = localStorage.getItem('is_admin') === 'true';
  const myEmail = (localStorage.getItem('email') || '').toLowerCase();

  useEffect(() => {
    const token = localStorage.getItem('Token');
    const headers = { Authorization: `Token ${token}` };
    Promise.all([
      api.get('/users/user-list/', { headers }),
      api.get('/users/names-departments/', { headers }),
    ]).then(([usersRes, profilesRes]) => {
      const profiles = profilesRes.data || [];
      const merged = (usersRes.data || [])
        .map(u => {
          const p = profiles.find(pr => pr.user_id === u.id);
          if (!p || !p.name) return null;
          return {
            id: u.id,
            email: u.email,
            name: p.name,
            department: p.department || '',
            designation: p.designation || '',
            photo_url: p.photo_url || null,
            date_of_birth: p.date_of_birth || null,
            date_of_joining: p.date_of_joining || null,
            employee_number: p.employee_number || '',
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));

      const list = isAdmin ? merged : merged.filter(e => e.email.toLowerCase() === myEmail);
      setEmployees(list);
      if (list.length > 0) setSelected(list[0]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [isAdmin, myEmail]);

  const filtered = employees.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    (e.department || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.designation || '').toLowerCase().includes(search.toLowerCase())
  );

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  const handlePrint = useCallback(() => {
    if (!selected) return;
    const empId = selected.employee_number || `EMP-${String(selected.id).padStart(4, '0')}`;
    const photoSrc = selected.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selected.name || '?')}&background=1e295c&color=fff&size=256&bold=true`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>ID Card – ${selected.name}</title>
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box;margin:0;padding:0}
  body{display:flex;justify-content:center;align-items:center;min-height:100vh;background:#e2e6ec;font-family:'Poppins',system-ui,sans-serif}
  .card{position:relative;width:330px;border-radius:24px;overflow:hidden;background:#fff;box-shadow:0 25px 50px -12px rgba(15,23,42,0.35)}
  .top{background:radial-gradient(circle at 50% 0%,#1e295c 0%,#0a1128 75%);padding:22px 20px 28px;text-align:center;color:#fff}
  .hdr{display:flex;align-items:center;gap:8px}
  .logo-mark{width:26px;height:26px;border-radius:6px;background:linear-gradient(135deg,#2dd4bf,#4f46e5);display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .co{font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;text-align:left;color:rgba(255,255,255,0.9)}
  .gold-line{height:2px;margin:12px -20px 0;background:linear-gradient(90deg,transparent,#f6e17a 20%,#fff3c4 50%,#f6e17a 80%,transparent)}
  .ring{width:124px;height:124px;margin:22px auto 0;border-radius:50%;padding:5px;background:conic-gradient(from 180deg at 50% 50%,#b8860b,#ffd700,#fff8dc,#daa520,#8b6508,#ffd700,#b8860b);box-shadow:0 10px 25px rgba(0,0,0,0.4)}
  .ring img{width:100%;height:100%;border-radius:50%;object-fit:cover;display:block;border:2px solid #0a1128}
  .ename{margin:14px 0 2px;font-size:21px;font-weight:800;color:#fff}
  .role{font-size:12px;font-weight:500;color:rgba(255,255,255,0.7);margin:0 0 14px}
  .dept{display:inline-block;padding:6px 20px;border-radius:99px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#1e1e1e;background:linear-gradient(180deg,#fff 0%,#e2e8f0 50%,#cbd5e1 100%);box-shadow:0 4px 10px rgba(0,0,0,0.2),inset 0 1px 1px rgba(255,255,255,0.9)}
  .bottom{position:relative;padding:24px 20px 20px;background:repeating-linear-gradient(100deg,rgba(255,255,255,0.5) 0px,rgba(255,255,255,0) 1px,rgba(0,0,0,0.04) 2px,rgba(255,255,255,0) 3px),linear-gradient(135deg,#f8fafc 0%,#cbd5e1 55%,#eef2f6 100%);display:flex;flex-direction:column;gap:14px;overflow:hidden}
  .row{display:flex;align-items:center;gap:12px}
  .badge{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:15px}
  .rt{font-size:13px;font-weight:600;color:#1e293b;word-break:break-all}
  .footer{position:relative;background:repeating-linear-gradient(100deg,rgba(255,255,255,0.4) 0px,rgba(255,255,255,0) 1px,rgba(0,0,0,0.05) 2px,rgba(255,255,255,0) 3px),linear-gradient(180deg,#cbd5e1 0%,#94a3b8 100%);padding:12px 16px;display:flex;align-items:center;justify-content:space-between;border-top:1px solid rgba(255,255,255,0.4);overflow:hidden}
  .ft{font-size:10px;font-weight:700;letter-spacing:.05em;color:#334155;text-transform:uppercase}
  .seal{width:44px;height:44px;margin-top:-26px;border-radius:14px;background:linear-gradient(160deg,#24356b,#0a1128);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 8px rgba(0,0,0,0.3)}
  @media print{body{background:white}.card{box-shadow:none}}
</style></head><body>
<div class="card">
  <div class="top">
    <div class="hdr">
      <img src="${window.location.origin}${logoImg}" alt="4Sight" style="width:26px;height:26px;border-radius:6px;object-fit:contain;background:#fff;padding:2px;">
      <div class="co">4SIGHT RESEARCH &amp; ANALYTICS FZCO</div>
    </div>
    <div class="gold-line"></div>
    <div class="ring"><img src="${photoSrc}" alt="${selected.name}"></div>
    <h2 class="ename">${selected.name}</h2>
    <p class="role">${selected.designation || '—'}</p>
    ${selected.department ? `<span class="dept">${selected.department.toUpperCase()}</span>` : ''}
  </div>
  <div class="bottom">
    <div class="row"><div class="badge" style="background:#dbeafe">✉️</div><div class="rt">${selected.email}</div></div>
    <div class="row"><div class="badge" style="background:#e0e7ff">🪪</div><div class="rt">${empId}</div></div>
    ${fmtDate(selected.date_of_birth) ? `<div class="row"><div class="badge" style="background:#fef3c7">🎂</div><div class="rt">${fmtDate(selected.date_of_birth)}</div></div>` : ''}
    ${fmtDate(selected.date_of_joining) ? `<div class="row"><div class="badge" style="background:#ede9fe">📅</div><div class="rt">${fmtDate(selected.date_of_joining)}</div></div>` : ''}
  </div>
  <div class="footer">
    <span class="ft">4sightglobal.com</span>
    <div class="seal"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round"><path d="M3 21V12a9 9 0 0 1 18 0v9"/><path d="M7 21v-7a5 5 0 0 1 10 0v7"/></svg></div>
    <span class="ft">Employee ID</span>
  </div>
</div>
<script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>
</body></html>`;

    const win = window.open('', '_blank', 'width=500,height=700');
    win.document.write(html);
    win.document.close();
  }, [selected, fmtDate]);

  const hasMissing = selected && (!selected.photo_url || !selected.date_of_birth || !selected.date_of_joining || !selected.employee_number);

  const startCapture = () => {
    setCaptureEmpNum(selected?.employee_number || '');
    setCaptureDob(selected?.date_of_birth || '');
    setCaptureDoj(selected?.date_of_joining || '');
    setCapturePhoto(null);
    setCapturing(true);
  };

  const handleSaveCapture = async () => {
    if (!selected) return;
    setCaptureSaving(true);
    const token = localStorage.getItem('Token');
    try {
      if (capturePhoto) {
        const fd = new FormData();
        fd.append('photo', capturePhoto);
        fd.append('user_id', selected.id);
        const res = await api.post('users/upload-photo/', fd, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Token ${token}` },
        });
        selected.photo_url = res.data.photo_url;
      }
      if (captureDob || captureDoj) {
        await api.put(`users/profile/update/${selected.id}/`, {
          name: selected.name,
          department: selected.department,
          designation: selected.designation,
          reporting_to: [],
          employee_number: captureEmpNum || selected.employee_number || null,
          date_of_birth: captureDob || selected.date_of_birth || null,
          date_of_joining: captureDoj || selected.date_of_joining || null,
        }, { headers: { Authorization: `Token ${token}` } });
        if (captureEmpNum) selected.employee_number = captureEmpNum;
        if (captureDob) selected.date_of_birth = captureDob;
        if (captureDoj) selected.date_of_joining = captureDoj;
      }
      setEmployees(prev => prev.map(e => e.id === selected.id ? { ...selected } : e));
      setSelected({ ...selected });
      setCapturing(false);
    } catch { alert('Save failed. Please try again.'); }
    finally { setCaptureSaving(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: '36px 28px', fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>ID Card Generator</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 5 }}>
            {isAdmin ? 'Select an employee to preview and print their ID card.' : 'Preview and print your employee ID card.'}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '260px 1fr' : '1fr', gap: 28, alignItems: 'start' }}>
          {/* Employee list (admin only) */}
          {isAdmin && (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                  type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search employees…"
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: '#fafafa' }}
                />
              </div>
              <div style={{ maxHeight: 440, overflowY: 'auto' }}>
                {loading ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading…</div>
                ) : filtered.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No results</div>
                ) : filtered.map(e => (
                  <div key={e.id} onClick={() => setSelected(e)}
                    style={{
                      padding: '10px 14px', cursor: 'pointer',
                      borderBottom: '1px solid #f8fafc',
                      background: selected?.id === e.id ? '#eef2ff' : 'transparent',
                      transition: 'background 0.12s',
                      borderLeft: selected?.id === e.id ? '3px solid #4f46e5' : '3px solid transparent',
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: selected?.id === e.id ? '#4f46e5' : '#0f172a' }}>{e.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{e.designation || e.department || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Card preview + actions */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: isAdmin ? 'flex-start' : 'center', gap: 20 }}>
            {loading && !selected ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            ) : selected ? (
              <>
                <IDCard employee={selected} />

                {/* Missing details banner */}
                {isAdmin && hasMissing && !capturing && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 16px', width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: 12.5, color: '#92400e', fontWeight: 600, marginBottom: 6 }}>
                      ⚠️ Some details are missing
                    </div>
                    <div style={{ fontSize: 12, color: '#78350f', marginBottom: 10 }}>
                      {!selected.employee_number && <div>• Employee number not set</div>}
                      {!selected.photo_url && <div>• Photo not uploaded</div>}
                      {!selected.date_of_birth && <div>• Date of birth not set</div>}
                      {!selected.date_of_joining && <div>• Date of joining not set</div>}
                    </div>
                    <button onClick={startCapture}
                      style={{ padding: '6px 14px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Fill Missing Details
                    </button>
                  </div>
                )}

                {/* Inline capture form */}
                {capturing && (
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 14 }}>Fill Missing Details</div>

                    {!selected.employee_number && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Employee Number</label>
                        <input type="text" value={captureEmpNum} onChange={e => setCaptureEmpNum(e.target.value)} placeholder="e.g. EMP-001"
                          style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }} />
                      </div>
                    )}

                    {!selected.photo_url && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Photo</label>
                        <input ref={photoInputRef} type="file" accept="image/*" onChange={e => setCapturePhoto(e.target.files[0])}
                          style={{ fontSize: 13, color: '#475569' }} />
                        {capturePhoto && <div style={{ fontSize: 11.5, color: '#059669', marginTop: 4 }}>✓ {capturePhoto.name}</div>}
                      </div>
                    )}

                    {!selected.date_of_birth && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Date of Birth</label>
                        <input type="date" value={captureDob} onChange={e => setCaptureDob(e.target.value)}
                          style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', fontFamily: 'inherit' }} />
                      </div>
                    )}

                    {!selected.date_of_joining && (
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Date of Joining</label>
                        <input type="date" value={captureDoj} onChange={e => setCaptureDoj(e.target.value)}
                          style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', fontFamily: 'inherit' }} />
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={handleSaveCapture} disabled={captureSaving}
                        style={{ padding: '8px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {captureSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button onClick={() => setCapturing(false)}
                        style={{ padding: '8px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handlePrint}
                    style={{
                      padding: '10px 24px', border: 'none', borderRadius: 9,
                      background: '#0f172a', color: '#fff',
                      fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                    🖨️ Print / Save as PDF
                  </button>
                </div>
                <p style={{ fontSize: 11.5, color: '#94a3b8', margin: 0 }}>
                  Use "Save as PDF" in the print dialog to download a PDF copy.
                </p>
              </>
            ) : (
              <div style={{ color: '#94a3b8', fontSize: 14 }}>Select an employee to preview their ID card.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
